import { InfoIcon, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Loader from "../../../../components/Loader/Loader";
import ReadersService from "../../../../services/ReadersService";
import { customStyles } from "../../../../components/Modal/ModalComponent";
import { debounce } from "../../../../utils/timer";
import Select from "../../../../components/Select/Select";
import List from "../../../../components/List/List";
import { getReaderModelByEnum, nonSynchronizableReaders, ReaderModel } from "../../../../constants/readers-model";
import { IoAlertCircleOutline, IoClose } from "react-icons/io5";
import Modal from 'react-modal';
import Button from "../../../../components/Button/Button";
import SyncIcon from "../../../../components/SyncIcon/SyncIcon";
import DeleteButton from "../../../../components/DeleteButton/DeleteButton";
import { TbTrashOff } from "react-icons/tb";

export function CondoUserReaders({ condoUser, condoUserLicense, unitId, toggleExpanded, updateUserReaders }) {
   const [isLoading, setIsLoading] = useState(false);
   const [modalReadersIsOpen, setModalReadersIsOpen] = useState(false);
   const [searchReader, setSearchReader] = useState("");
   const [readerModel, setReaderModel] = useState(`0`);
   const [readers, setReaders] = useState([]);
   const [selectedReaders, setSelectedReaders] = useState([]);

   function closeReadersModal() {
      setReaders([]);
      setSearchReader();
      setSelectedReaders([]);
      setModalReadersIsOpen(false);
   }

   const getReadersByUserAndCondominium = async () => {
      setIsLoading(true);
      const response = await ReadersService.listReadersByUserAndCondominium(unitId, condoUser.id);
      const currentNoSaved = condoUserLicense?.readers?.filter(ur => ['draft', 'deleting'].includes(ur.status)) ?? [];
      updateUserReaders([
         ...currentNoSaved,
         ...response.data.filter(r => !currentNoSaved.some(ur => ur.readerId === r.id)),
      ], unitId);
      setIsLoading(false);
   };

   useEffect(() => {
      if (condoUser) {
         getReadersByUserAndCondominium();
      }
   }, [condoUser, unitId]);

   async function fetchReaders(search, modelArg) {
      try {
         const model = modelArg !== '0' ? modelArg : null;
         const response = await ReadersService.getPaginatedReaders(1, search, model, unitId, 'name', null, true);
         return response.data?.items;
      } catch (error) {
         console.error('Error fetching readers:', error);
         return [];
      }
   }

   const fetchReadersDebounce = useCallback(
      debounce(fetchReaders, 700), []
   );

   useEffect(() => {
      async function loadReaders() {
         const readers = await fetchReadersDebounce(searchReader, readerModel);
         setReaders(readers);
      }
      loadReaders();
   }, [searchReader]);

   useEffect(() => {
      async function loadReaders() {
         const readers = await fetchReaders(searchReader, readerModel);
         setReaders(readers);
      }
      loadReaders();
   }, [readerModel, modalReadersIsOpen]);

   function onCheckAllChange(e) {
      if (e.target.checked) {
         setSelectedReaders(readers);
         return;
      }
      setSelectedReaders([]);
   }

   function onCheckReaderChange(reader) {
      if (selectedReaders.find(sReader => sReader.uuid === reader.uuid)) {
         setSelectedReaders(selectedReaders.filter(r => r.uuid !== reader.uuid));
         return;
      }
      setSelectedReaders([
         ...selectedReaders,
         reader,
      ]);
   }

   function onConfirmSelect() {
      const newUserReaders = [...(condoUserLicense.readers ?? [])];
      for (const selectedReader of selectedReaders) {
         if (newUserReaders.some(ur => ur.readerId === selectedReader.id)) {
            continue;
         }
         newUserReaders.push({
            reader: selectedReader,
            readerId: selectedReader.id,
            status: 'draft',
         });
      }
      updateUserReaders(newUserReaders, unitId);
      setSelectedReaders([]);
      setModalReadersIsOpen(false);
   }

   function handleClickRemove(userReader) {
      updateUserReaders(
         condoUserLicense.readers
            .filter(ur => ur.status !== 'draft' || ur.readerId !== userReader.readerId)
            .map(ur => ({
               ...ur,
               _oldStatus_: ur._oldStatus_ ?? ur.status,
               status: ur.readerId === userReader.readerId ? 'deleting' : ur.status,
            })),
         unitId
      );
   }

   function handleCancelDelete(userReader) {
      updateUserReaders(
         condoUserLicense.readers
            .map(ur => ({
               ...ur,
               _oldStatus_: ur.readerId === userReader.readerId ? null : ur._oldStatus_,
               status: ur.readerId === userReader.readerId ? ur._oldStatus_ : ur.status,
            })),
         unitId
      );
   }

   const isEmptyReaders = !condoUserLicense.readers || condoUserLicense.readers.length === 0;

   return (
      <>
         <div className="p-4! shadow-inner">
            {isLoading ? (
               <div className="flex justify-center items-center gap-2">
                  <div className="**:m-0! **:size-5!"><Loader /></div>
                  <span className="text-(--textColor)">Carregando</span>
               </div>
            ) : (
               <>
                  <h3 className="text-(--textColor) flex items-center gap-1.5 w-full! mb-3! opacity-40">
                     <InfoIcon size={16} />
                     Gerencie abaixo a sincronização dos dados do usuário nos dispositivos de leitura desta licença.
                  </h3>
                  <div className="flex flex-wrap justify-start gap-2">
                     {condoUserLicense.readers?.map(userReader => (
                        <div className="w-40 h-30 relative group">
                           <div className="size-full transform-3d perspective-distant group-hover:rotate-y-180 whitespace-break-spaces duration-500">
                              <div className={`absolute inset-0 inline-flex flex-col size-full justify-between gap-2 items-start p-3.5! box-border! border! rounded-lg border-(--borderColor)! transform-3d backface-hidden!`}>
                                 <span className="w-full flex flex-col gap-1.5">
                                    <span className="leading-[1.1]!">{userReader.reader.name}</span>
                                    <span className="opacity-45 text-[11px]! font-extrabold! tracking-wider!">
                                       {getReaderModelByEnum(userReader.reader.model)?.name}
                                    </span>
                                 </span>
                                 <div className="w-10 border-t! border-(--borderColor)! my-0.5!"></div>
                                 <SyncIcon
                                    showLabel={true}
                                    syncStatus={userReader.status}
                                    lastOperation={userReader.lastOperation}
                                    data={userReader}
                                    listEntityName={`condo-user-readers`}
                                 />
                              </div>
                              <div className={`absolute inset-0 inline-flex flex-col size-full justify-between gap-2 items-start p-3.5! box-border! border! rounded-lg border-(--borderColor)! transform-3d backface-hidden! rotate-y-180!`}>
                                 <span className="w-full flex flex-col gap-1.5">
                                    <span className="leading-[1.1]!">{userReader.reader.name}</span>
                                 </span>
                                 {userReader.status !== 'deleting' ? (
                                    <DeleteButton
                                       className="button-white tab-condo-users-buttons-visibility"
                                       handleDelete={() => handleClickRemove(userReader)}
                                    />
                                 ) : (
                                    <Button
                                       className={"button-red"}
                                       label={"Cancelar"}
                                       onClick={() => handleCancelDelete(userReader)}
                                       type="button"
                                       icon={<TbTrashOff size={18} />}
                                    />
                                 )}
                              </div>
                           </div>
                        </div>
                     ))}
                     <button
                        type="button"
                        className={`
                        ${isEmptyReaders ? 'inline-flex flex-row w-full h-fit' : 'inline-flex flex-col w-40 h-30'}
                        justify-center gap-2 items-center p-2! box-border! border! rounded-lg border-dashed! border-(--borderColor) cursor-pointer text-center
                        hover:border-(--primaryColor) hover:text-(--primaryColor) hover:bg-(--primaryColorLight) duration-200
                     `}
                        onClick={() => setModalReadersIsOpen(true)}
                     >
                        <Plus size={isEmptyReaders ? 18 : 28} />
                        <span className={`${isEmptyReaders ? 'w-fit' : 'w-full'} whitespace-break-spaces leading-0`}>Vincular dispositivo</span>
                     </button>
                  </div>
               </>
            )}
         </div>
         <Modal
            isOpen={modalReadersIsOpen}
            onRequestClose={() => closeReadersModal()}
            style={{
               ...customStyles,
               content: {
                  ...customStyles.content,
                  width: '700px',
                  maxWidth: '90dvw',
                  padding: '36px'
               }
            }}
            contentLabel="Selecione as Leitoras"
         >
            <div className='modal-header mb-4!'>
               <h2>Selecione as Leitoras</h2>
               <button className="close-button" onClick={() => closeReadersModal()}><IoClose /></button>
            </div>
            <div className='modal-body'>
               <div className='alert alert-warning'>
                  <IoAlertCircleOutline size={20} />
                  <span><b>Atenção:</b> Se você não encontrar sua leitora na lista, verifique se o usuário em questão possui algum grupo com acesso vinculado à leitora.</span>
               </div>
               <div className='grid grid-cols-2 w-full gap-2 mb-0.5!'>
                  <input
                     placeholder="Buscar Leitoras"
                     className="input-form search-input w-full h-[44px]!"
                     type="text"
                     value={searchReader}
                     onChange={(e) => setSearchReader(e.target.value)}
                     style={{ backgroundColor: `var(--inputBackgroundColor)` }}
                  />
                  <Select
                     value={readerModel}
                     onChange={({ target }) => setReaderModel(target.value)}
                     required={true}
                     options={[
                        { value: `0`, text: 'Busque por modelo' },
                        ...Object.entries(ReaderModel)
                           .filter(([, model]) => !nonSynchronizableReaders.includes(model.key))
                           .map(([, model]) => (
                              { value: model.key, text: model.name }
                           )),
                     ]}
                  />
               </div>
               <div className="readers-list-wrapper">
                  <List
                     listEntityName={"readers"}
                     selectableRows={false}
                     collumsObjIndex={[
                        {
                           collumnLabel: "",
                           type: "custom",
                           custom: ({ row }) => (
                              <input type='checkbox' checked={selectedReaders?.find(r => r.uuid === row.uuid)} onChange={(e) => onCheckReaderChange(row)} />
                           ),
                           // select all
                           customHeader: () => (
                              <input type='checkbox' className="-ml-[8px]!" onChange={(e) => onCheckAllChange(e)} checked={selectedReaders?.length === readers?.length} />
                           )
                        },
                        {
                           collumnLabel: "Nome",
                           index: "name",
                           type: "text",
                        },
                        {
                           collumnLabel: "Modelo",
                           index: "model",
                           type: "text",
                           custom: ({ row }) => getReaderModelByEnum(row.model)?.name
                        },
                        {
                           collumnLabel: "Tipo",
                           index: "configuration.authenticationType",
                           type: "text",
                        },
                     ]}
                     data={readers}
                  />
               </div>
               <div className="modal-buttons-container">
                  <Button
                     onClick={() => onConfirmSelect()}
                     label={"Confirmar"}
                  />
                  <Button
                     className={"button-white"}
                     onClick={() => closeReadersModal()}
                     label={"Cancelar"}
                  />
               </div>
            </div>
         </Modal>
      </>
   );
}